if (!window.Eurus.loadedScript.has('product-sibling.js')) {
  window.Eurus.loadedScript.add('product-sibling.js');

  /* ------------------------------------------------------------------
   * Keep the shopper's selected size(s) when they switch between
   * product siblings.
   *
   * Siblings are separate products, so a variant id can't carry the
   * choice across them. Instead we remember the selected option VALUES
   * (e.g. "M") and re-apply them to the sibling's variant picker,
   * reusing the theme's own `xVariantSelect.updateVariantSelector` so
   * price / availability / add-to-cart stay in sync.
   *
   * Multi-option products (e.g. the Everyday Set bundle: "Bralette
   * (Size)" + a bottom "(Size)") are matched option-by-option:
   *   1. by option NAME  – exact match (colour swaps, same garment)
   *   2. by option POSITION – fallback when the name differs across
   *      styles (e.g. "Classic Thong (Size)" -> "High Waist Briefs
   *      (Size)"), so the size still carries when it's offered.
   * A carried value is only applied when the target option actually
   * offers it, so unrelated options are never mismatched.
   *
   * Works for both linking options:
   *   - "load"     (AJAX swap)   -> restore right after the swap
   *   - "redirect" (page reload) -> stash in sessionStorage, restore on load
   * ------------------------------------------------------------------ */
  const SIBLING_CARRY_KEY = 'eurus:sibling-carried-options';
  const SIBLING_CARRY_TTL = 60000; // ms – ignore stale hand-offs

  const cssEsc = (v) => (window.CSS && CSS.escape ? CSS.escape(v) : String(v).replace(/["\\\]]/g, '\\$&'));

  // The main product's variant picker (never the quick-view / card ones).
  function getMainPicker() {
    const mp = document.querySelector('.main-product');
    if (!mp) return null;
    return mp.querySelector('.variant-selects-main');
  }

  // True once Alpine has initialised the picker AND the store the variant
  // update path relies on. On a cold page load the component can exist a
  // few ms before `xHelper` is registered, so we must wait for both.
  function themeReady(picker) {
    try {
      if (!(window.Alpine && Alpine.$data && Alpine.store)) return false;
      const data = Alpine.$data(picker);
      if (!data || typeof data.updateVariantSelector !== 'function') return false;
      if (!Alpine.store('xHelper')) return false;
      return true;
    } catch (e) {
      return false;
    }
  }

  // The non-colour options of the current main picker, in position order.
  function collectTargetOptions(picker) {
    const targets = [];
    picker.querySelectorAll('fieldset[data-option-name]').forEach((fs) => {
      if (fs.querySelector('.color-watches')) return; // skip colour swatches
      targets.push({ name: fs.dataset.optionName, index: fs.dataset.optionIndex, type: 'radio', el: fs });
    });
    picker.querySelectorAll('[data-option-name] select').forEach((sel) => {
      const wrap = sel.closest('[data-option-name]');
      if (!wrap || wrap.querySelector('.color-watches')) return;
      targets.push({ name: wrap.dataset.optionName, index: wrap.dataset.optionIndex, type: 'select', el: sel });
    });
    return targets;
  }

  // Read the currently selected non-colour option values from the main picker.
  function captureMainProductOptions() {
    try {
      const picker = getMainPicker();
      if (!picker) return null;
      const options = [];
      collectTargetOptions(picker).forEach((t) => {
        if (t.type === 'radio') {
          const checked = t.el.querySelector('input.input-radio:checked');
          if (checked) options.push({ name: t.name, index: t.index, value: checked.value });
        } else {
          const opt = t.el.options[t.el.selectedIndex];
          if (opt) options.push({ name: t.name, index: t.index, value: opt.value });
        }
      });
      return options.length ? options : null;
    } catch (e) {
      return null;
    }
  }

  // Pair each carried option with a target option: by name first, then by
  // position for whatever is left over.
  function matchOptions(desired, targets) {
    const usedTargets = new Set();
    const plan = [];
    const claim = (d, predicate) => {
      const t = targets.find((o) => !usedTargets.has(o) && predicate(o));
      if (t) {
        usedTargets.add(t);
        plan.push({ target: t, value: d.value });
        return true;
      }
      return false;
    };
    const leftover = desired.filter((d) => !claim(d, (o) => o.name === d.name));
    leftover.forEach((d) => {
      if (d.index != null) claim(d, (o) => o.index === d.index);
    });
    return plan;
  }

  // Apply the carried options to the (now current) main picker.
  // Returns: 'waiting'     – theme not ready yet, keep polling
  //          'unavailable' – no carried option maps to an offered value here
  //          'confirmed'   – every mapped option already on the wanted value
  //          'applied'     – changed one or more options and fired the update
  function applySelection(desired) {
    const picker = getMainPicker();
    if (!picker || !themeReady(picker)) return 'waiting';

    const targets = collectTargetOptions(picker);
    if (targets.length === 0) return 'unavailable';

    const plan = matchOptions(desired, targets);

    let presentCount = 0;
    let lastId = null;
    const changes = [];

    for (const { target, value } of plan) {
      if (target.type === 'radio') {
        const input = Array.from(target.el.querySelectorAll('input.input-radio')).find((i) => i.value === value);
        if (!input) continue; // this value isn't offered on the sibling
        presentCount++;
        lastId = input.id;
        if (!input.checked) changes.push({ el: input, value, type: 'radio' });
      } else {
        const opt = Array.from(target.el.options).find((o) => o.value === value);
        if (!opt) continue;
        presentCount++;
        lastId = target.el.id;
        if (target.el.value !== value) changes.push({ el: target.el, value, type: 'select' });
      }
    }

    if (presentCount === 0) return 'unavailable';
    if (changes.length === 0) return 'confirmed';

    changes.forEach(({ el, value, type }) => {
      if (type === 'select') {
        Array.from(el.options).forEach((o) => o.removeAttribute('selected'));
        const opt = Array.from(el.options).find((o) => o.value === value);
        if (opt) opt.setAttribute('selected', 'selected');
        el.value = value;
      } else {
        el.checked = true;
      }
      lastId = el.id;
    });

    // Drive the theme's own variant update directly (no event-timing race).
    try {
      Alpine.$data(picker).updateVariantSelector(lastId, '');
    } catch (e) {
      const el = lastId && picker.querySelector('#' + cssEsc(lastId));
      if (el) el.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return 'applied';
  }

  // Poll until the theme is ready, apply, then keep watching briefly and
  // re-assert if anything resets the selection. Times out gracefully.
  function restoreCarriedSelection(desired) {
    if (!desired || !desired.length) return;
    const start = Date.now();
    let lastChange = Date.now();
    const tick = () => {
      let state = 'waiting';
      try {
        state = applySelection(desired);
      } catch (e) {}
      if (state === 'unavailable') return; // nothing here to restore
      if (state === 'applied') lastChange = Date.now();
      // Stop once the wanted selection has held steady for ~1s.
      if (state === 'confirmed' && Date.now() - lastChange > 1000) return;
      if (Date.now() - start > 8000) return; // hard timeout
      setTimeout(tick, 120);
    };
    setTimeout(tick, 0);
  }

  // On a fresh page load (redirect siblings), consume any stashed selection.
  (function restoreOnLoad() {
    let desired = null;
    try {
      const raw = sessionStorage.getItem(SIBLING_CARRY_KEY);
      if (raw) {
        sessionStorage.removeItem(SIBLING_CARRY_KEY); // one-shot
        const data = JSON.parse(raw);
        if (data && Array.isArray(data.options) && Date.now() - data.t < SIBLING_CARRY_TTL) {
          desired = data.options;
        }
      }
    } catch (e) {}
    if (desired) restoreCarriedSelection(desired);
  })();

  requestAnimationFrame(() => {
    document.addEventListener("alpine:init", () => {
      Alpine.data("xProductSibling", (sectionId, isProductPage, redirect) => ({
        cachedResults: [],
        updateProductInfo(url) {
          // Remember the shopper's current size(s) before we leave / swap.
          const carried = captureMainProductOptions();

          if (redirect) {
            if (carried) {
              try {
                sessionStorage.setItem(SIBLING_CARRY_KEY, JSON.stringify({ options: carried, t: Date.now() }));
              } catch (e) {}
            }
            window.location.href = url;
            return
          }
          const link = isProductPage?`${url}`:`${url}?section_id=${sectionId}&page=25000`;

          if (this.cachedResults[link]) {
            const html = this.cachedResults[link];
            this._handleSwapProduct(html);
            restoreCarriedSelection(carried);
          } else {
            fetch(link)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              this._updateTitle(html);
              this._handleSwapProduct(html);
              this._updateFbtContainer(html)
              this.cachedResults[link] = html;
              restoreCarriedSelection(carried);
            })
          }
          this._updateURL(url);
        },
        changeSelectOption(event) {
          const input = event.target.selectedOptions[0];
          const targetUrl = input.dataset.productUrl;
          this.updateProductInfo(targetUrl);
        },
        _updateURL(url) {
          if (!isProductPage) return;
          window.history.replaceState({}, '', `${url}`);
        },
        _updateTitle(html) {
          if (!isProductPage) return;
          document.querySelector('head title').textContent = html.querySelector('.product-title').textContent;
          const destination = document.getElementById('breadcrumbs--' + sectionId);
          const source = html.getElementById('breadcrumbs--' + sectionId);
          if (source && destination) destination.innerHTML = source.innerHTML;
        },
        _updateFbtContainer(html) {
          if (!isProductPage) return;
          const destination = document.getElementById('popup-fbt-' + sectionId);
          const source = html.getElementById('popup-fbt-' + sectionId);
          if (source && destination) destination.outerHTML = source.outerHTML;
        },
        _handleSwapProduct(html) {
          const destination = isProductPage ? document.querySelector('.main-product') : document.querySelector('.x-product-' + sectionId);
          const source = isProductPage ? html.querySelector('.main-product') : html.querySelector('.x-product-' + sectionId);
          if (source && destination) {
            if (!isProductPage) destination.id = source.id;
            destination.innerHTML = source.innerHTML;
          }
        }
      }));
    });
  });
}